
import Base from '@/api-services/base'
const BaseURL = `TiposIncidencias`;

/**
 * Reglas semánticas para resolver íconos de tipos de incidencia.
 * Se evalúan en orden: la primera coincidencia gana.
 * Reglas más específicas van antes que las genéricas.
 */
const _reglasIcono = [
  // — Específicas compuestas (evaluar antes que las genéricas) —
  { palabras: ["home office"],                        icono: "fas fa-home" },
  { palabras: ["descanso trabajado"],                 icono: "fas fa-briefcase" },
  { palabras: ["festivo trabajado"],                  icono: "fas fa-briefcase" },
  { palabras: ["doble turno", "turno doble"],         icono: "fas fa-briefcase" },
  { palabras: ["asistencia doble"],                   icono: "fas fa-briefcase" },
  { palabras: ["olvido de checada"],                  icono: "fas fa-clock" },
  { palabras: ["tiempo x tiempo", "tiempo por tiempo"], icono: "fas fa-exchange-alt" },
  { palabras: ["sin goce", "asunto personal"],        icono: "fas fa-user" },
  { palabras: ["riesgo de trabajo"],                  icono: "fas fa-hard-hat" },
  { palabras: ["falta injustificada"],                icono: "fas fa-times-circle" },
  { palabras: ["permiso para llegar tarde"],          icono: "fas fa-hourglass-half" },

  // — Vacaciones / descanso —
  { palabras: ["vacacion"],                           icono: "fas fa-umbrella-beach" },
  { palabras: ["descanso"],                           icono: "fas fa-bed" },

  // — Incapacidades —
  { palabras: ["maternidad"],                         icono: "fas fa-baby" },
  { palabras: ["paternidad"],                         icono: "fas fa-baby" },
  { palabras: ["incapacidad"],                        icono: "fas fa-notes-medical" },

  // — Permisos específicos —
  { palabras: ["matrimonio", "boda"],                 icono: "fas fa-ring" },
  { palabras: ["defuncion", "defunción", "fallecimiento"], icono: "fas fa-heart-broken" },
  { palabras: ["reconocimiento", "premio"],           icono: "fas fa-trophy" },
  { palabras: ["viaje de trabajo", "comision", "comisión"], icono: "fas fa-plane" },
  { palabras: ["capacitacion", "capacitación", "curso"], icono: "fas fa-graduation-cap" },
  { palabras: ["covid", "contingencia"],              icono: "fas fa-virus" },
  { palabras: ["salud", "doctor", "médico", "medico"], icono: "fas fa-stethoscope" },

  // — Faltas / sanciones —
  { palabras: ["sancion", "sanción"],                 icono: "fas fa-gavel" },
  { palabras: ["suspension", "suspensión"],           icono: "fas fa-ban" },
  { palabras: ["falta"],                              icono: "fas fa-times-circle" },
  { palabras: ["retardo"],                            icono: "fas fa-hourglass-half" },

  // — Operativas —
  { palabras: ["baja"],                               icono: "fas fa-user-minus" },
  { palabras: ["brigada"],                            icono: "fas fa-users" },
  { palabras: ["cambio de turno"],                    icono: "fas fa-sync-alt" },
  { palabras: ["festivo"],                            icono: "fas fa-star" },
  { palabras: ["sismo", "emergencia", "huracan", "huracán"], icono: "fas fa-exclamation-triangle" },
  { palabras: ["checador", "reloj"],                  icono: "fas fa-fingerprint" },
  { palabras: ["solidario"],                          icono: "fas fa-hands-helping" },
  { palabras: ["entrada"],                            icono: "fas fa-sign-in-alt" },
  { palabras: ["salida"],                             icono: "fas fa-sign-out-alt" },

  // — Genéricos (catch-all) —
  { palabras: ["permiso"],                            icono: "fas fa-clipboard-check" },
];

/** Mapa de fallback por abreviatura (último recurso) */
const _abreviaturaIcono = {
  V:   "fas fa-umbrella-beach",
  HO:  "fas fa-home",
  I:   "fas fa-notes-medical",
  OC:  "fas fa-clock",
  PSG: "fas fa-user",
  TX:  "fas fa-exchange-alt",
  X:   "fas fa-bed",
  B:   "fas fa-trophy",
  U:   "fas fa-ring",
  F:   "fas fa-times-circle",
  D:   "fas fa-bed",
  S:   "fas fa-ban",
  W:   "fas fa-heart-broken",
  IE:  "fas fa-notes-medical",
  IM:  "fas fa-baby",
  IR:  "fas fa-hard-hat",
  IT:  "fas fa-notes-medical",
  CP:  "fas fa-graduation-cap",
  CT:  "fas fa-sync-alt",
  PG:  "fas fa-clipboard-check",
  PS:  "fas fa-user",
  DT:  "fas fa-briefcase",
  FT:  "fas fa-briefcase",
  SR:  "fas fa-gavel",
  PR:  "fas fa-hourglass-half",
  FF:  "fas fa-heart-broken",
  SU:  "fas fa-ban",
};

const ICONO_DEFAULT = "fas fa-calendar-day";

export default {
  GetsResponse: null,
  Listado: [],
  FotoURL(Registro) {
    return "/img/grupos.svg";
  },

  /**
   * Devuelve un ícono FontAwesome para un tipo de incidencia.
   * Resolución: 1) contexto semántico de etiqueta/descripción, 2) abreviatura, 3) default.
   * @param {Object|null} tipo - Objeto con { etiqueta, descripcion, abreviatura }
   * @returns {string} Clase CSS de FontAwesome
   */
  Icono(tipo) {
    if (!tipo) return ICONO_DEFAULT;

    // Construir texto normalizado para búsqueda semántica
    const texto = [tipo.etiqueta || "", tipo.descripcion || ""]
      .join(" ")
      .toLowerCase()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, ""); // quitar acentos para comparar

    // Evaluar reglas semánticas en orden de prioridad
    for (const regla of _reglasIcono) {
      for (const palabra of regla.palabras) {
        const palabraNorm = palabra.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        if (texto.includes(palabraNorm)) {
          return regla.icono;
        }
      }
    }

    // Fallback por abreviatura
    if (tipo.abreviatura && _abreviaturaIcono[tipo.abreviatura]) {
      return _abreviaturaIcono[tipo.abreviatura];
    }

    return ICONO_DEFAULT;
  },

  gets(Vista, BaseGetsListado, OnCorrecto, OnErroneo, OnFinalizado) {

    return Base.getsBaseSuscripcion(Vista, BaseURL, BaseGetsListado, OnCorrecto, OnErroneo, OnFinalizado);
  },
  getsSelect(Vista, OnCorrecto, OnErroneo, OnFinalizado) {
    if (this.GetsResponse != null)
      OnCorrecto(this.GetsResponse);
    return Base.getsBaseSuscripcion(Vista, BaseURL, { PaginaLen: 1000 }, (response) => {
      this.GetsResponse = response;
      this.Listado = response.data.listado;
      if (OnCorrecto != undefined)
        OnCorrecto(response);

    }, OnErroneo, OnFinalizado);
  },
  put(Vista, ID, Datos, OnCorrecto, OnErroneo, OnFinalizado) {
    var URL = Base.ObtenURLSuscripcion(`${BaseURL}/${ID}`);
    return Base.put(Vista, URL, Datos, {}, OnCorrecto, OnErroneo, OnFinalizado);
  },
  post(Vista, Datos, OnCorrecto, OnErroneo, OnFinalizado) {
    return Base.postBaseSuscripcion(Vista, BaseURL, Datos, {}, OnCorrecto, OnErroneo, OnFinalizado);
  },
  get(Vista, id, OnCorrecto, OnErroneo, OnFinalizado) {
    return Base.getsBaseSuscripcion(Vista, `${BaseURL}/${id}`, {}, OnCorrecto, OnErroneo, OnFinalizado);
  },
  patch(Vista, id, OnCorrecto, OnErroneo, OnFinalizado) {
    return Base.patchBaseSuscripcion(Vista, `${BaseURL}/${id}`,{},{}, OnCorrecto, OnErroneo, OnFinalizado);
  },
  delete(Vista, id, OnCorrecto, OnErroneo, OnFinalizado) {
    return Base.deleteBaseSuscripcion(Vista, `${BaseURL}/${id}`, OnCorrecto, OnErroneo, OnFinalizado);
  },
  putCampos(Vista, Campos, OnCorrecto, OnErroneo, OnFinalizado) {
    var URL = Base.ObtenURLSuscripcion(`${BaseURL}/Campos`);
    return Base.put(
      Vista,
      URL,
      Campos,
      {},
      OnCorrecto,
      OnErroneo,
      OnFinalizado
    );
  },
  getAvanzado(Vista, id, OnCorrecto, OnErroneo, OnFinalizado) {
    return Base.getsBaseSuscripcion(
      Vista,
      `${BaseURL}/${id}/Avanzado`,
      {},
      OnCorrecto,
      OnErroneo,
      OnFinalizado
    );
  },
}
